import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react"
import { useConfig } from "@/config/context"
import {
  buildFavoriteRelaysDraft,
  buildRelaySetDraft,
  newRelaySetId,
  parseFavoriteRelaysEvent,
  parseRelaySetEvent,
} from "@/nostr/relaySets"
import { eventAddress, KINDS, type RelayDescriptor, type RelaySet } from "@/nostr/types"
import { preferredLookupRelays, simplifyUrl, uniqueRelayUrls } from "@/utils/url"
import { readStorage, StorageKeys, writeStorage } from "@/utils/storage"
import { useAuth } from "./AuthContext"
import { useEventVersion, useNostr } from "./NostrContext"
import { useToast } from "./ToastContext"

export interface RelayContextValue {
  relaySets: RelaySet[]
  favoriteRelays: string[]
  /** Relays the pool keeps connected: favourites, sets and configured defaults. */
  appRelays: string[]
  /** Relays used when publishing relay library changes. */
  writeTargets: string[]
  isReady: boolean
  isFavorite: (url: string) => boolean
  addFavoriteRelays: (urls: string[]) => Promise<void>
  removeFavoriteRelays: (urls: string[]) => Promise<void>
  reorderFavoriteRelays: (urls: string[]) => Promise<void>
  createRelaySet: (name: string, relayUrls?: string[]) => Promise<RelaySet | undefined>
  updateRelaySet: (set: RelaySet) => Promise<void>
  deleteRelaySet: (id: string) => Promise<void>
  addRelaysToSet: (setId: string, urls: string[]) => Promise<void>
  removeRelaysFromSet: (setId: string, urls: string[]) => Promise<void>
  reorderRelaySets: (sets: RelaySet[]) => Promise<void>
}

const RelayContext = createContext<RelayContextValue | undefined>(undefined)

interface CachedRelayLibrary {
  favoriteRelays: string[]
  relaySets: RelaySet[]
}

function loadCachedLibrary(fallbackRelays: string[]): CachedRelayLibrary {
  const stored = readStorage<Partial<CachedRelayLibrary>>(StorageKeys.relaySets, {})
  const sets = Array.isArray(stored.relaySets)
    ? stored.relaySets
        .filter((set) => Boolean(set?.id && set?.name && Array.isArray(set.relayUrls)))
        .map((set) => ({ ...set, relayUrls: uniqueRelayUrls(set.relayUrls) }))
    : []
  const favorites = Array.isArray(stored.favoriteRelays)
    ? uniqueRelayUrls(stored.favoriteRelays)
    : uniqueRelayUrls(fallbackRelays)
  return { favoriteRelays: favorites, relaySets: sets }
}

export function RelayProvider({ children }: { children: ReactNode }) {
  const config = useConfig()
  const { pool, store } = useNostr()
  const { pubkey, signer } = useAuth()
  const { push } = useToast()
  const community = config.community

  const presetSets = useMemo<RelaySet[]>(
    () =>
      community.relaySets.map((set) => ({
        id: set.id,
        name: set.name,
        relayUrls: uniqueRelayUrls(set.relayUrls),
        preset: true,
      })),
    [community.relaySets],
  )

  const [library, setLibrary] = useState<CachedRelayLibrary>(() =>
    community.mode
      ? { relaySets: [], favoriteRelays: uniqueRelayUrls(community.extraRelays) }
      : loadCachedLibrary(config.relays.defaultRelays),
  )
  const [isReady, setIsReady] = useState(false)

  const relaySets = useMemo(() => {
    const local = community.mode ? [] : library.relaySets
    return [
      ...presetSets,
      ...local.filter((set) => !presetSets.some((preset) => preset.id === set.id)),
    ]
  }, [community.mode, library.relaySets, presetSets])

  const favoriteRelays = useMemo(() => {
    const communityRelays = community.mode ? uniqueRelayUrls(community.extraRelays) : []
    return uniqueRelayUrls([...communityRelays, ...library.favoriteRelays])
  }, [community.mode, community.extraRelays, library.favoriteRelays])

  const appRelays = useMemo(
    () =>
      uniqueRelayUrls([
        ...favoriteRelays,
        ...relaySets.flatMap((set) => set.relayUrls),
        ...config.relays.defaultRelays,
      ]),
    [favoriteRelays, relaySets, config.relays.defaultRelays],
  )

  const writeTargets = useMemo(
    () => uniqueRelayUrls([...favoriteRelays, ...config.relays.defaultRelays]),
    [favoriteRelays, config.relays.defaultRelays],
  )

  // Keep the pool connected to exactly the relays the user cares about.
  useEffect(() => {
    const descriptors: RelayDescriptor[] = appRelays.map((url) => ({
      url,
      read: true,
      write: true,
      priority: 100,
    }))
    pool.setRelays(descriptors)
  }, [appRelays, pool])

  // Signed-in users get their relay library back from the relays themselves.
  useEffect(() => {
    if (community.mode || !pubkey) {
      setIsReady(true)
      return
    }
    let cancelled = false
    const subscription = pool.subscribe(
      [
        { kinds: [KINDS.FAVORITE_RELAYS], authors: [pubkey], limit: 1 },
        { kinds: [KINDS.RELAY_SETS], authors: [pubkey], limit: 100 },
      ],
      {
        relays: preferredLookupRelays(writeTargets, 3),
        onEvent: (event) => store.add(event),
        onEose: () => {
          if (!cancelled) setIsReady(true)
        },
      },
    )
    return () => {
      cancelled = true
      subscription.close()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pubkey, community.mode, pool, store])

  const storeVersion = useEventVersion(store)
  useEffect(() => {
    if (community.mode || !pubkey) return
    const favoriteEvent = store.getReplaceable(KINDS.FAVORITE_RELAYS, pubkey)
    const setEvents = store
      .values()
      .filter((event) => event.kind === KINDS.RELAY_SETS && event.pubkey === pubkey)

    if (!favoriteEvent && setEvents.length === 0) return

    const parsed = favoriteEvent ? parseFavoriteRelaysEvent(favoriteEvent) : { relayUrls: [], setIds: [] }
    const sets = setEvents
      .map((event) => parseRelaySetEvent(event))
      .filter((set): set is RelaySet => Boolean(set))
    const ordered = parsed.setIds
      .map((id) => sets.find((set) => set.id === id))
      .filter((set): set is RelaySet => Boolean(set))
    const remaining = sets
      .filter((set) => !ordered.some((item) => item.id === set.id))
      .sort((a, b) => a.name.localeCompare(b.name))

    setLibrary({ favoriteRelays: parsed.relayUrls, relaySets: [...ordered, ...remaining] })
    setIsReady(true)
  }, [storeVersion, pubkey, community.mode, store])

  // ------------------------------------------------------------------ helpers

  const publishFavoriteRelays = useCallback(
    async (favorites: string[], sets: RelaySet[]): Promise<boolean> => {
      if (!signer || !pubkey) return false
      const aTags = sets
        .filter((set) => !set.preset && set.aTag)
        .map((set) => set.aTag as string[])
      const draft = buildFavoriteRelaysDraft(favorites, aTags)
      try {
        const signed = await signer.signEvent(draft)
        const summary = await pool.publish(signed, writeTargets)
        if (summary.accepted === 0) {
          push("error", "No relay accepted the relay list update", summarizePublish(summary))
          return false
        }
        store.add(signed)
        if (summary.rejected > 0) {
          push("info", "Relay list saved, but some relays rejected it", summarizePublish(summary))
        }
        return true
      } catch (error) {
        push("error", "Could not publish the relay list", errorMessage(error))
        return false
      }
    },
    [signer, pubkey, pool, writeTargets, push, store],
  )

  const assertEditable = useCallback((): boolean => {
    if (community.mode) {
      push("info", "This relay library is managed by the site administrator")
      return false
    }
    return true
  }, [community.mode, push])

  // --------------------------------------------------------------- favourites

  const addFavoriteRelays = useCallback(
    async (urls: string[]) => {
      if (!assertEditable()) return
      const cleaned = uniqueRelayUrls(urls).filter((url) => !favoriteRelays.includes(url))
      if (cleaned.length === 0) return
      const next: CachedRelayLibrary = {
        ...library,
        favoriteRelays: [...library.favoriteRelays, ...cleaned],
      }
      if (!signer || !pubkey) {
        setLibrary(next)
        return
      }
      if (await publishFavoriteRelays(next.favoriteRelays, relaySets)) setLibrary(next)
    },
    [assertEditable, favoriteRelays, library, relaySets, signer, pubkey, publishFavoriteRelays],
  )

  const removeFavoriteRelays = useCallback(
    async (urls: string[]) => {
      if (!assertEditable()) return
      const remove = new Set(urls)
      const next: CachedRelayLibrary = {
        ...library,
        favoriteRelays: library.favoriteRelays.filter((url) => !remove.has(url)),
      }
      if (!signer || !pubkey) {
        setLibrary(next)
        return
      }
      if (await publishFavoriteRelays(next.favoriteRelays, relaySets)) setLibrary(next)
    },
    [assertEditable, library, relaySets, signer, pubkey, publishFavoriteRelays],
  )

  const reorderFavoriteRelays = useCallback(
    async (urls: string[]) => {
      if (!assertEditable()) return
      const next: CachedRelayLibrary = { ...library, favoriteRelays: uniqueRelayUrls(urls) }
      if (!signer || !pubkey) {
        setLibrary(next)
        return
      }
      if (await publishFavoriteRelays(next.favoriteRelays, relaySets)) setLibrary(next)
    },
    [assertEditable, library, relaySets, signer, pubkey, publishFavoriteRelays],
  )

  useEffect(() => {
    if (community.mode) return
    writeStorage(StorageKeys.relaySets, library)
  }, [community.mode, library])

  // --------------------------------------------------------------- relay sets

  const upsertRelaySet = useCallback(
    async (set: RelaySet): Promise<boolean> => {
      if (!assertEditable()) return false
      const withoutSet = library.relaySets.filter((item) => item.id !== set.id)
      const next: CachedRelayLibrary = {
        ...library,
        relaySets: [...withoutSet, set].sort((a, b) => a.name.localeCompare(b.name)),
      }
      if (!signer || !pubkey) {
        setLibrary(next)
        return true
      }
      try {
        const signed = await signer.signEvent(buildRelaySetDraft(set))
        const summary = await pool.publish(signed, writeTargets)
        if (summary.accepted === 0) {
          push("error", "No relay accepted the relay set update", summarizePublish(summary))
          return false
        }
        store.add(signed)
        const nextSet: RelaySet = { ...set, aTag: ["a", eventAddress(signed)] }
        const withATag = next.relaySets.map((item) => (item.id === set.id ? nextSet : item))
        if (!(await publishFavoriteRelays(next.favoriteRelays, withATag))) return false
        setLibrary({ ...next, relaySets: withATag })
        return true
      } catch (error) {
        push("error", "Could not publish the relay set", errorMessage(error))
        return false
      }
    },
    [assertEditable, library, signer, pubkey, pool, writeTargets, store, push, publishFavoriteRelays],
  )

  const createRelaySet = useCallback(
    async (name: string, relayUrls: string[] = []): Promise<RelaySet | undefined> => {
      const trimmed = name.trim()
      if (!trimmed) {
        push("error", "Give the relay set a name")
        return undefined
      }
      const set: RelaySet = {
        id: newRelaySetId(trimmed),
        name: trimmed,
        relayUrls: uniqueRelayUrls(relayUrls),
      }
      const ok = await upsertRelaySet(set)
      return ok ? set : undefined
    },
    [upsertRelaySet, push],
  )

  const updateRelaySet = useCallback(
    async (set: RelaySet) => {
      await upsertRelaySet(set)
    },
    [upsertRelaySet],
  )

  const deleteRelaySet = useCallback(
    async (id: string) => {
      if (!assertEditable()) return
      const set = library.relaySets.find((item) => item.id === id)
      if (!set) return
      const next: CachedRelayLibrary = {
        ...library,
        relaySets: library.relaySets.filter((item) => item.id !== id),
      }
      if (!signer || !pubkey) {
        setLibrary(next)
        return
      }
      if (await publishFavoriteRelays(next.favoriteRelays, next.relaySets)) {
        setLibrary(next)
        push("success", `Removed the relay set “${set.name}”`)
      }
    },
    [assertEditable, library, signer, pubkey, publishFavoriteRelays, push],
  )

  const addRelaysToSet = useCallback(
    async (setId: string, urls: string[]) => {
      const set = library.relaySets.find((item) => item.id === setId)
      if (!set) return
      await upsertRelaySet({ ...set, relayUrls: uniqueRelayUrls([...set.relayUrls, ...urls]) })
    },
    [library.relaySets, upsertRelaySet],
  )

  const removeRelaysFromSet = useCallback(
    async (setId: string, urls: string[]) => {
      const set = library.relaySets.find((item) => item.id === setId)
      if (!set) return
      const remove = new Set(urls)
      await upsertRelaySet({ ...set, relayUrls: set.relayUrls.filter((url) => !remove.has(url)) })
    },
    [library.relaySets, upsertRelaySet],
  )

  const reorderRelaySets = useCallback(
    async (sets: RelaySet[]) => {
      if (!assertEditable()) return
      const next: CachedRelayLibrary = { ...library, relaySets: sets }
      if (!signer || !pubkey) {
        setLibrary(next)
        return
      }
      if (await publishFavoriteRelays(next.favoriteRelays, sets)) setLibrary(next)
    },
    [assertEditable, library, signer, pubkey, publishFavoriteRelays],
  )

  const isFavorite = useCallback((url: string) => favoriteRelays.includes(url), [favoriteRelays])

  const value = useMemo<RelayContextValue>(
    () => ({
      relaySets,
      favoriteRelays,
      appRelays,
      writeTargets,
      isReady,
      isFavorite,
      addFavoriteRelays,
      removeFavoriteRelays,
      reorderFavoriteRelays,
      createRelaySet,
      updateRelaySet,
      deleteRelaySet,
      addRelaysToSet,
      removeRelaysFromSet,
      reorderRelaySets,
    }),
    [
      relaySets,
      favoriteRelays,
      appRelays,
      writeTargets,
      isReady,
      isFavorite,
      addFavoriteRelays,
      removeFavoriteRelays,
      reorderFavoriteRelays,
      createRelaySet,
      updateRelaySet,
      deleteRelaySet,
      addRelaysToSet,
      removeRelaysFromSet,
      reorderRelaySets,
    ],
  )

  return <RelayContext.Provider value={value}>{children}</RelayContext.Provider>
}

export function useRelays(): RelayContextValue {
  const context = useContext(RelayContext)
  if (!context) throw new Error("useRelays must be used inside a RelayProvider")
  return context
}

export function summarizePublish(summary: {
  results: Array<{ relay: string; ok: boolean; message: string }>
}): string {
  return summary.results
    .map((result) => `${simplifyUrl(result.relay)}: ${result.ok ? "ok" : result.message || "rejected"}`)
    .join(" · ")
}

export function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error)
}
